import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useZendeskLocalization } from '@repo/poly-hooks/zendesk-localization';
import { fetchJsonFromS3, getArticleIdFromConfig, getZendeskArticleUrl } from '#utils/zendesk';
import { useSentry } from '@repo/runtime-kit/sentry/context';
import { isNetworkError } from '@repo/runtime-kit/utils/is-network-error';

/**
 * Shared hook for Zendesk article fetching.
 *
 * This hook:
 * - Fetches the S3 JSON data (country - article ID mapping)
 * - Gets service data (country, help center URL)
 * - Handles Sentry error reporting
 * - Extracts URL from S3 data or falls back to local config
 *
 * @param articleSlug - The article slug
 * @returns string - The article URL
 *
 */
export function useZendeskArticle(articleSlug: string): string {
  const sentry = useSentry();
  const zendesk = useZendeskLocalization();

  const { data, error } = useQuery({
    queryKey: ['zendesk-article-s3-json', articleSlug],
    queryFn: () => fetchJsonFromS3(articleSlug),
    staleTime: Infinity,
    // Retry up to 3 times so transient S3 blips self-heal without reaching Sentry.
    // A persistent failure (e.g. unpublished slug) will exhaust retries and alert.
    retry: 3,
  });

  const helpCenterUrl = zendesk.getLocalizedHelpCenter;
  const organizationCountry = zendesk.organizationCountry;

  const url = useMemo(
    () =>
      getZendeskArticleUrl({
        articleSlug,
        helpCenterUrl,
        organizationCountry,
        s3Data: data,
      }),
    [articleSlug, helpCenterUrl, organizationCountry, data]
  );

  useEffect(() => {
    // The S3 mapping is not the only source of truth: getZendeskArticleUrl also
    // falls back to the local ZENDESK_ARTICLES_IDS config. When that fallback
    // resolves an article ID for the org's country, the returned URL is still
    // correct and the user is unaffected — so an S3 failure here is noise, not
    // an actionable error (e.g. a slug served only from config returns a 403
    // because its JSON has not been published to the bucket). We only report
    // when there is no config fallback, meaning the URL genuinely degraded to
    // the bare help-center page.
    const hasConfigFallback = Boolean(getArticleIdFromConfig(articleSlug, organizationCountry));

    if (error && !isNetworkError(error) && !hasConfigFallback) {
      sentry.captureMessage(`Failed to fetch Zendesk article "${articleSlug}"`, {
        level: 'error',
        extra: {
          articleSlug,
          error,
          organizationCountry,
        },
      });
    }
  }, [articleSlug, error, organizationCountry, sentry]);

  return url;
}
